/**
 * API Service
 * Handles all communication with the FastAPI backend
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

class AgentAPI {
  constructor() {
    this.sessionId = this.getOrCreateSessionId();
    this.authToken = this.getAuthToken();
  }

  /**
   * Get or create a session ID for conversation continuity
   */
  getOrCreateSessionId() {
    let sessionId = sessionStorage.getItem('sessionId');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('sessionId', sessionId);
    }
    return sessionId;
  }

  /**
   * Get authentication token from URL query parameter or sessionStorage
   */
  getAuthToken() {
    // First check if we already have it in sessionStorage
    let token = sessionStorage.getItem('authToken');
    
    if (!token) {
      // Try to get it from URL query parameters
      const urlParams = new URLSearchParams(window.location.search);
      token = urlParams.get('token');
      
      if (token) {
        // Store it for future use
        sessionStorage.setItem('authToken', token);
        
        // Optional: Clean up the URL to remove the token for security
        // This prevents the token from appearing in browser history
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);
      }
    }
    
    return token;
  }

  /**
   * Get authorization headers
   * Returns Bearer token if available, otherwise empty
   */
  getAuthHeaders() {
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (this.authToken) {
      headers['Authorization'] = `Bearer ${this.authToken}`;
    }
    
    return headers;
  }

  /**
   * Send a chat message and get complete response
   */
  async sendMessage(message) {
    const response = await fetch(`${API_BASE_URL}/api/v1/chat`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({
        message,
        session_id: this.sessionId,
        stream: false
      })
    });

    if (!response.ok) {
      throw new Error(`API error: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Send a message and stream the response
   * @param {string} message - User message
   * @param {function} onChunk - Callback for each chunk received
   */
  async streamMessage(message, onChunk) {
    const response = await fetch(`${API_BASE_URL}/api/v1/chat/stream`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({
        message,
        session_id: this.sessionId,
        stream: true
      })
    });

    if (!response.ok) {
      throw new Error(`API error: ${response.statusText}`);
    }

    // Read the stream
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    
    // Buffer for accumulating incomplete messages
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          // Process any remaining data in buffer
          if (buffer.trim()) {
            this.processSSELine(buffer, onChunk);
          }
          break;
        }

        // Decode the chunk and add to buffer
        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;

        // Process complete lines
        const lines = buffer.split('\n');
        
        // Keep the last line in buffer (might be incomplete)
        buffer = lines.pop() || '';
        
        // Process all complete lines
        for (const line of lines) {
          if (line.trim()) {
            this.processSSELine(line, onChunk);
          }
        }
      }
    } catch (error) {
      console.error('Stream reading error:', error);
      onChunk({
        type: 'error',
        content: `Stream error: ${error.message}`,
        recoverable: false
      });
    } finally {
      reader.releaseLock();
    }
  }

  /**
   * Process a single SSE line
   */
  processSSELine(line, onChunk) {
    // SSE format: "data: {json}"
    if (line.startsWith('data: ')) {
      const jsonStr = line.slice(6).trim();
      
      // Skip empty data or special SSE signals
      if (!jsonStr || jsonStr === '[DONE]') {
        return;
      }
      
      try {
        const data = JSON.parse(jsonStr);
        const sanitizedData = this.sanitizeData(data);
        onChunk(sanitizedData);
      } catch (parseError) {
        console.error('Error parsing complete SSE message:', parseError);
        console.error('Failed JSON string:', jsonStr.substring(0, 500) + '...');
        
        // Attempt recovery with cleaned string
        try {
          const cleanedStr = jsonStr
            .replace(/\bNaN\b/g, 'null')
            .replace(/\bInfinity\b/g, 'null')
            .replace(/\b-Infinity\b/g, 'null')
            .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, ''); // Remove control characters
            
          const data = JSON.parse(cleanedStr);
          const sanitizedData = this.sanitizeData(data);
          console.warn('Recovered from malformed JSON with sanitization');
          onChunk(sanitizedData);
        } catch (recoveryError) {
          console.error('Failed to recover from malformed JSON:', recoveryError);
          
          // Check if this might be a large message that needs special handling
          if (jsonStr.length > 10000) {
            onChunk({
              type: 'error',
              content: 'Large message parsing failed. The data might be too large for streaming.',
              recoverable: true,
              hint: 'Consider breaking large responses into smaller chunks on the backend.'
            });
          } else {
            onChunk({
              type: 'error',
              content: 'Failed to parse server response',
              recoverable: true
            });
          }
        }
      }
    } else if (line.startsWith('event:')) {
      // Handle SSE event type if your backend sends them
      console.log('SSE Event:', line.slice(6).trim());
    } else if (line.startsWith('id:')) {
      // Handle SSE message ID if needed
      console.log('SSE ID:', line.slice(3).trim());
    } else if (line.startsWith('retry:')) {
      // Handle SSE retry instruction if needed
      console.log('SSE Retry:', line.slice(6).trim());
    }
  }

  /**
   * Sanitize data to ensure it's safe to process
   * Handles NaN, Infinity, and other problematic values
   */
  sanitizeData(data) {
    if (data === null || data === undefined) {
      return data;
    }

    if (Array.isArray(data)) {
      return data.map(item => this.sanitizeData(item));
    }

    if (typeof data === 'object') {
      const sanitized = {};
      for (const key in data) {
        const value = data[key];
        // Check for problematic float values
        if (typeof value === 'number') {
          if (isNaN(value) || !isFinite(value)) {
            sanitized[key] = null;
          } else {
            sanitized[key] = value;
          }
        } else {
          sanitized[key] = this.sanitizeData(value);
        }
      }
      return sanitized;
    }

    // Check for problematic number values
    if (typeof data === 'number') {
      if (isNaN(data) || !isFinite(data)) {
        return null;
      }
    }

    return data;
  }

  /**
   * Get session information
   */
  async getSessionInfo() {
    const response = await fetch(`${API_BASE_URL}/api/v1/session/${this.sessionId}`, {
      headers: this.getAuthHeaders()
    });
    if (!response.ok) {
      if (response.status === 404) {
        return null; // Session doesn't exist yet
      }
      throw new Error(`API error: ${response.statusText}`);
    }
    return response.json();
  }

  /**
   * Download an artifact (Excel file, etc.)
   */
  async downloadArtifact(fileType) {
    const headers = {};
    if (this.authToken) {
      headers['Authorization'] = `Bearer ${this.authToken}`;
    }
    
    const response = await fetch(
      `${API_BASE_URL}/api/v1/download/${this.sessionId}/${fileType}`,
      { headers }
    );
    if (!response.ok) {
      throw new Error(`Download failed: ${response.statusText}`);
    }
    return response.blob();
  }

  /**
   * Upload a .pptx template for the agent to use when generating presentations.
   */
  async uploadTemplate(file) {
    const form = new FormData();
    form.append('session_id', this.sessionId);
    form.append('file', file);
    const headers = {};
    if (this.authToken) {
      headers['Authorization'] = `Bearer ${this.authToken}`;
    }
    const response = await fetch(`${API_BASE_URL}/api/v1/upload/template`, {
      method: 'POST',
      headers,
      body: form,
    });
    if (!response.ok) {
      throw new Error(`Upload failed: ${response.statusText}`);
    }
    return response.json();
  }

  /**
   * Health check
   */
  async checkHealth() {
    const response = await fetch(`${API_BASE_URL}/health`);
    return response.json();
  }

  /**
   * Validate authentication token
   * @param {string} token - The token to validate
   * @returns {Promise<object>} Validation result with valid, authenticated properties
   */
  async validateToken(token) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/validate-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token })
      });

      if (!response.ok) {
        return { valid: false, authenticated: false, message: 'Token validation failed' };
      }

      return response.json();
    } catch (error) {
      console.error('Token validation error:', error);
      return { valid: false, authenticated: false, message: error.message };
    }
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated() {
    return !!this.authToken;
  }

  /**
   * Reset session (start new conversation)
   */
  resetSession() {
    this.sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('sessionId', this.sessionId);
    return this.sessionId;
  }
}

export default new AgentAPI();